import { jsxs, jsx } from 'react/jsx-runtime';
import { Calendar } from './calendar.js';
import { Popover, PopoverTrigger, PopoverContent } from './popover.js';
import { cn } from '../../utils/formatters.js';
import '@radix-ui/react-label';
import { Button } from './button.js';
import '@radix-ui/react-progress';
import '../../contexts/AppContext.js';
import { CalendarIcon } from 'lucide-react';
import * as React from 'react';
import '../../lib/toast.js';
import './alert.js';
import '@radix-ui/react-avatar';
import './badge.js';
import './chart.js';
import '@radix-ui/react-checkbox';
import '@radix-ui/react-collapsible';
import 'cmdk';
import '@radix-ui/react-dialog';
import 'vaul';
import '@radix-ui/react-dropdown-menu';
import './radio-group.js';
import '@radix-ui/react-select';
import '@radix-ui/react-separator';
import './sidebar.js';
import 'react-toastify';
import '@radix-ui/react-switch';
import '@radix-ui/react-tabs';
import '@radix-ui/react-tooltip';
import 'react-router-dom';
import '../page-layout/FormPageLayout.js';
import '../form/RichTextEditor.js';
import '../../contexts/OrganizationContext.js';
import 'axios';
import '@tanstack/react-query';
import '../crud/CrudDetailView.js';
import '../crud/CrudFormView.js';
import '../../contexts/SidebarContext.js';
import '../../contexts/ThemeContext.js';
import '@dnd-kit/core';
import '@dnd-kit/sortable';
import '@dnd-kit/utilities';

/**
 * Date picker component with calendar dropdown
 * Handles UTC date conversion to ensure consistent date storage
 * @param date - Selected date value (Date object or ISO string)
 * @param onDateChange - Callback when date changes (returns Date in UTC)
 * @param placeholder - Placeholder text when no date selected
 * @param disabled - Whether the picker is disabled
 * @param className - Additional CSS classes
 * @param useUtc - Whether to use UTC dates (defaults to true)
 */
function DatePicker({ date, onDateChange, placeholder = 'Select date', disabled = false, className, useUtc = true, }) {
    const [open, setOpen] = React.useState(false);
    // Parse the date value to a Date object
    const parsedDate = React.useMemo(() => {
        if (!date)
            return undefined;
        if (date instanceof Date)
            return date;
        return new Date(date);
    }, [date]);
    // For UTC mode, create a local-equivalent date so the calendar highlights the correct day
    const displayDate = React.useMemo(() => {
        if (!parsedDate || !useUtc)
            return parsedDate;
        return new Date(parsedDate.getUTCFullYear(), parsedDate.getUTCMonth(), parsedDate.getUTCDate());
    }, [parsedDate, useUtc]);
    // Initialize the calendar month to show the selected date or current month
    const defaultMonth = displayDate || new Date();
    /**
     * Convert local date selection to UTC date at midnight
     */
    const handleDateSelect = (selectedDate) => {
        if (!selectedDate) {
            onDateChange?.(undefined);
            setOpen(false);
            return;
        }
        if (useUtc) {
            // Create a UTC date at midnight for the selected day
            const utcDate = new Date(Date.UTC(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 0, 0, 0, 0));
            onDateChange?.(utcDate);
        }
        else {
            onDateChange?.(selectedDate);
        }
        setOpen(false);
    };
    return (jsxs(Popover, { open: open, onOpenChange: setOpen, children: [jsx(PopoverTrigger, { asChild: true, children: jsxs(Button, { variant: "outline", disabled: disabled, className: cn('w-full justify-start text-left font-normal', !parsedDate && 'text-muted-foreground', className), children: [jsx(CalendarIcon, { className: "mr-2 h-4 w-4" }), parsedDate
                            ? useUtc
                                ? parsedDate.toLocaleDateString(undefined, { timeZone: 'UTC' })
                                : parsedDate.toLocaleDateString()
                            : placeholder] }) }), jsx(PopoverContent, { className: "w-auto overflow-hidden p-0", align: "start", children: jsx(Calendar, { mode: "single", selected: displayDate, onSelect: handleDateSelect, disabled: disabled, captionLayout: "dropdown", fromYear: 1900, toYear: 2100, defaultMonth: defaultMonth }) })] }));
}

export { DatePicker };
//# sourceMappingURL=date-picker.js.map
